/**
 * Vet clinic ZIP / city search helpers (KAH-980).
 * Pure filter + nearest-store ranking for the vet landing page.
 * Reuses dental-quiz-core lookupZip / miles; loaded by the browser and by Jest.
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.KdVetClinicZipSearch = factory();
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";

  var LIST_MILES = 60;
  var NEAREST_N = 4;

  function isFiveDigitZip(query) {
    return /^\d{5}$/.test(String(query || "").trim());
  }

  function normalizeSearch(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  }

  function buildSearchText(event) {
    return normalizeSearch(
      [event.store, event.address, event.state, event.zip, event.handle, event.city].join(" ")
    );
  }

  function substringFilter(events, query) {
    var normalized = normalizeSearch(query);
    if (!normalized) {
      return Array.isArray(events) ? events.slice() : [];
    }
    return (events || []).filter(function (ev) {
      return buildSearchText(ev).indexOf(normalized) !== -1;
    });
  }

  function uniqueHandles(events) {
    var seen = {};
    var handles = [];
    (events || []).forEach(function (ev) {
      var handle = String((ev && ev.handle) || "").trim();
      if (!handle || seen[handle]) {
        return;
      }
      seen[handle] = true;
      handles.push(handle);
    });
    return handles;
  }

  function buildEventsZipFallback(events) {
    var map = {};
    (events || []).forEach(function (ev) {
      var handle = String((ev && ev.handle) || "").trim();
      if (!handle || map[handle]) {
        return;
      }
      var zip = String((ev && ev.zip) || "").trim();
      if (/^\d{5}/.test(zip)) {
        map[handle] = zip.slice(0, 5);
      }
    });
    return map;
  }

  function resolveStorePoint(handle, storesByHandle, eventZips, zipIndex, lookupZip) {
    var store = (storesByHandle && storesByHandle[handle]) || null;
    if (store && store.lat != null && store.lng != null && !isNaN(Number(store.lat)) && !isNaN(Number(store.lng))) {
      return { lat: Number(store.lat), lng: Number(store.lng) };
    }
    var zip = (store && store.zip) || (eventZips && eventZips[handle]) || null;
    if (!zip || typeof lookupZip !== "function") {
      return null;
    }
    return lookupZip(String(zip).slice(0, 5), zipIndex);
  }

  function rankNearestHandles(origin, handles, storesByHandle, eventZips, zipIndex, lookupZip, milesFn) {
    var ranked = handles
      .map(function (handle) {
        var point = resolveStorePoint(handle, storesByHandle, eventZips, zipIndex, lookupZip);
        return {
          handle: handle,
          miles: point && typeof milesFn === "function" ? milesFn(origin.lat, origin.lng, point.lat, point.lng) : Infinity
        };
      })
      .sort(function (a, b) {
        return a.miles - b.miles;
      });

    if (!ranked.length || !isFinite(ranked[0].miles)) {
      return [];
    }

    var cutoff = Math.max(LIST_MILES, ranked[0].miles + 30);
    return ranked
      .filter(function (store, index) {
        return index === 0 || store.miles <= cutoff;
      })
      .slice(0, NEAREST_N);
  }

  function eventsNearZip(input) {
    var events = input.events || [];
    var zip = String(input.query || "").trim().slice(0, 5);
    var lookupZip = input.lookupZip;
    var milesFn = input.miles;
    var zipIndex = input.zipIndex || {};
    var storesByHandle = input.storesByHandle || {};
    var eventZips = buildEventsZipFallback(events);

    var origin = typeof lookupZip === "function" ? lookupZip(zip, zipIndex) : null;
    if (!origin) {
      return {
        events: substringFilter(events, zip),
        mode: "substring",
        zipMatched: false,
        nearestMiles: null,
        unplacedZip: true
      };
    }

    var nearest = rankNearestHandles(
      origin,
      uniqueHandles(events),
      storesByHandle,
      eventZips,
      zipIndex,
      lookupZip,
      milesFn
    );

    if (!nearest.length) {
      return {
        events: substringFilter(events, zip),
        mode: "substring",
        zipMatched: true,
        nearestMiles: null,
        unplacedZip: false
      };
    }

    var milesByHandle = {};
    nearest.forEach(function (store) {
      milesByHandle[store.handle] = store.miles;
    });

    var rankedEvents = events
      .filter(function (ev) {
        return milesByHandle[String((ev && ev.handle) || "").trim()] != null;
      })
      .map(function (ev) {
        var handle = String(ev.handle || "").trim();
        var copy = {};
        for (var key in ev) {
          if (Object.prototype.hasOwnProperty.call(ev, key)) {
            copy[key] = ev[key];
          }
        }
        copy.mi = milesByHandle[handle];
        return copy;
      })
      .sort(function (a, b) {
        if (a.mi !== b.mi) {
          return a.mi - b.mi;
        }
        return new Date(a.start).getTime() - new Date(b.start).getTime();
      });

    return {
      events: rankedEvents,
      mode: "distance",
      zipMatched: true,
      nearestMiles: rankedEvents.length ? rankedEvents[0].mi : null,
      unplacedZip: false
    };
  }

  /**
   * Filter clinic events by search query.
   * 5-digit ZIP → nearest stores by haversine (when centroid exists).
   * Otherwise → substring match on store / city / address / zip.
   */
  function filterClinicEvents(input) {
    var events = (input && input.events) || [];
    var query = input && input.query != null ? String(input.query) : "";
    var trimmed = query.trim();

    if (!trimmed) {
      return {
        events: events.slice(),
        mode: "empty",
        zipMatched: null,
        nearestMiles: null,
        unplacedZip: false
      };
    }

    if (isFiveDigitZip(trimmed)) {
      return eventsNearZip(
        Object.assign({}, input, {
          events: events,
          query: trimmed
        })
      );
    }

    return {
      events: substringFilter(events, trimmed),
      mode: "substring",
      zipMatched: null,
      nearestMiles: null,
      unplacedZip: false
    };
  }

  return {
    LIST_MILES: LIST_MILES,
    NEAREST_N: NEAREST_N,
    isFiveDigitZip: isFiveDigitZip,
    normalizeSearch: normalizeSearch,
    buildSearchText: buildSearchText,
    substringFilter: substringFilter,
    filterClinicEvents: filterClinicEvents
  };
});
